import { useState, useEffect } from "react"
import { api, ApiError } from "../../lib/api"
import { useSelectedAccount } from "../../contexts/AccountContext"
import CreateOrganizationModal from "./CreateOrganizationModal"

const OrganizationsTab = () => {
  const { organizations, selectedOrganizationId, setSelectedOrganizationId, refreshOrganizations, selectedOrganizationRole } = useSelectedAccount()
  const isOwner = selectedOrganizationRole === "owner"

  const org = organizations.find(o => o.id === selectedOrganizationId) ?? null

  const [orgName, setOrgName] = useState(org?.name ?? "")
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [confirmingDeleteId, setConfirmingDeleteId] = useState<number | null>(null)
  const [deleting, setDeleting] = useState(false)

  // 選択組織が切り替わったら orgName を同期（saving中は上書きしない）
  useEffect(() => {
    if (!saving) {
      setOrgName(org?.name ?? "")
    }
  }, [org?.id, org?.name]) // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!org) return
    setSaving(true)
    setError(null)
    setSuccessMessage(null)
    try {
      await api.patch(`/organizations/${org.id}`, { organization: { name: orgName } })
      await refreshOrganizations()
      setSuccessMessage("組織名を更新しました")
    } catch (err) {
      const { data: errData } = err as ApiError
      setError(errData.errors?.join(", ") ?? "更新に失敗しました")
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (orgId: number) => {
    setDeleting(true)
    setError(null)
    try {
      await api.delete(`/organizations/${orgId}`)
      setConfirmingDeleteId(null)
      await refreshOrganizations()
    } catch (err) {
      const { data: errData } = err as ApiError
      setError(errData.error ?? "削除に失敗しました")
    } finally {
      setDeleting(false)
    }
  }

  const handleCreated = async (newOrgId: number) => {
    setShowCreateModal(false)
    await refreshOrganizations()
    setSelectedOrganizationId(newOrgId)
  }

  return (
    <div className="settings-card">
      <div className="settings-card__header">
        <div className="settings-card__header-title">
          <i className="bi bi-building" />
          <h2>組織管理</h2>
        </div>
        <button className="btn-primary btn-primary--sm" onClick={() => setShowCreateModal(true)}>
          <i className="bi bi-plus-lg" />
          組織を追加
        </button>
      </div>

      <div className="settings-card__body">
        {error && <div className="settings-alert settings-alert--error">{error}</div>}
        {successMessage && <div className="settings-alert settings-alert--success">{successMessage}</div>}

        {/* 複数組織リスト */}
        {organizations.length >= 2 && (
          <div className="org-list">
            {organizations.map(o => (
              <div
                key={o.id}
                className={`org-list__item${o.id === selectedOrganizationId ? " org-list__item--active" : ""}`}
              >
                <button
                  className="org-list__select"
                  onClick={() => { setSelectedOrganizationId(o.id); setError(null); setSuccessMessage(null) }}
                >
                  <i className="bi bi-building" />
                  <div className="org-list__info">
                    <span className="org-list__name">{o.name}</span>
                    <span className="org-list__meta">
                      メンバー: {o.active_members_count}名{"\u3000"}アカウント: {o.instagram_accounts_count}件{"\u3000"}{o.created_at}
                    </span>
                  </div>
                  {o.id === selectedOrganizationId && (
                    <span className="org-list__badge">選択中</span>
                  )}
                </button>

                {isOwner && (confirmingDeleteId === o.id ? (
                  <div className="org-list__confirm">
                    <span>本当に削除しますか？</span>
                    <button
                      className="btn-danger btn-danger--sm"
                      onClick={() => handleDelete(o.id)}
                      disabled={deleting}
                    >
                      {deleting ? "削除中..." : "削除する"}
                    </button>
                    <button className="btn-secondary btn-secondary--sm" onClick={() => setConfirmingDeleteId(null)}>
                      キャンセル
                    </button>
                  </div>
                ) : (
                  <button
                    className="btn-ghost btn-ghost--danger"
                    onClick={() => setConfirmingDeleteId(o.id)}
                    disabled={organizations.length <= 1}
                    title={organizations.length <= 1 ? "最後の組織は削除できません" : "組織を削除"}
                  >
                    <i className="bi bi-trash" />
                  </button>
                ))}
              </div>
            ))}
          </div>
        )}

        {/* 選択中組織の編集 */}
        <div className="org-layout">
          <div className="org-form">
            <div className="form-field">
              <label>組織名</label>
              {isOwner ? (
                <>
                  <p className="form-hint">この組織の表示名を変更できます</p>
                  <input
                    type="text"
                    value={orgName}
                    onChange={e => setOrgName(e.target.value)}
                    disabled={!org}
                  />
                </>
              ) : (
                <p className="form-value">{org?.name ?? "取得中..."}</p>
              )}
            </div>
            {isOwner && (
              <div className="settings-card__actions">
                <button className="btn-primary" onClick={handleSave} disabled={saving || !org}>
                  <i className="bi bi-check-lg" />
                  {saving ? "保存中..." : "変更を保存"}
                </button>
              </div>
            )}
          </div>

          <div className="org-info-card">
            <h3>
              <i className="bi bi-info-circle" />
              組織情報
            </h3>
            <dl>
              <dt>メンバー数</dt>
              <dd>{org ? `${org.active_members_count}名` : "取得中..."}</dd>
              <dt>アカウント数</dt>
              <dd>{org ? `${org.instagram_accounts_count}件` : "取得中..."}</dd>
              <dt>作成日</dt>
              <dd>{org ? org.created_at : "取得中..."}</dd>
            </dl>
          </div>
        </div>
      </div>

      {showCreateModal && (
        <CreateOrganizationModal
          onClose={() => setShowCreateModal(false)}
          onCreated={handleCreated}
        />
      )}
    </div>
  )
}

export default OrganizationsTab
